import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export type PasswordEditResult = { currentPassword: string; newPassword: string } | undefined;

@Component({
  selector: 'app-password-change-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './password-edit-dialog.component.html',
  styleUrls: ['./password-edit-dialog.component.scss']
})
export class PasswordEditDialogComponent {
  form: FormGroup;

  // Steuert die Sichtbarkeit der Felder
  showCurrent = false;
  showNew = false;
  showConfirm = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PasswordEditDialogComponent, PasswordEditResult>
  ) {
    this.form = this.fb.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validators: [this.matchPasswordsValidator, this.notSameAsCurrentValidator],
      }
    );
  }

// Validiert, dass confirmPassword === newPassword und setzt Fehler auf dem confirm-Feld
  private matchPasswordsValidator(group: AbstractControl): ValidationErrors | null {
    const newPwdCtrl = group.get('newPassword');
    const confirmCtrl = group.get('confirmPassword');
    if (!newPwdCtrl || !confirmCtrl) return null;

    const mismatch = newPwdCtrl.value && confirmCtrl.value && newPwdCtrl.value !== confirmCtrl.value;

    // Fehler direkt am confirm-Feld setzen/entfernen
    if (mismatch) {
      const prev = confirmCtrl.errors || {};
      confirmCtrl.setErrors({ ...prev, mismatch: true });
    } else if (confirmCtrl.hasError('mismatch')) {
      const { mismatch, ...rest } = confirmCtrl.errors || {};
      confirmCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }

    // Optional weiterhin auch als Form-Error zurückgeben (macht Button disablebar)
    return mismatch ? { mismatch: true } : null;
  }

// Verhindert, dass das neue Passwort mit dem aktuellen identisch ist
  private notSameAsCurrentValidator(group: AbstractControl): ValidationErrors | null {
    const currentCtrl = group.get('currentPassword');
    const newPwdCtrl  = group.get('newPassword');
    if (!currentCtrl || !newPwdCtrl) return null;

    const same = currentCtrl.value && newPwdCtrl.value && currentCtrl.value === newPwdCtrl.value;

    // Fehler direkt am newPassword-Feld setzen/entfernen
    if (same) {
      const prev = newPwdCtrl.errors || {};
      newPwdCtrl.setErrors({ ...prev, sameAsCurrent: true });
    } else if (newPwdCtrl.hasError('sameAsCurrent')) {
      const { sameAsCurrent, ...rest } = newPwdCtrl.errors || {};
      newPwdCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }

    // Optional auch als Form-Error zurückgeben
    return same ? { sameAsCurrent: true } : null;
  }


  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onSave(): void {
    if (this.form.valid) {
      const { currentPassword, newPassword } = this.form.value as { currentPassword: string; newPassword: string };
      this.dialogRef.close({ currentPassword, newPassword });
    }
  }
}
